// import { Divider } from "@chakra-ui/react";
import { Accordion } from "@kelp-dao/kernel-design-system";
import { toFixedWithoutRounding } from "@constants/common";
import useBridgeWrapConfig from "@hooks/useBridgeWrapConfig";
import useSwapTokensBalances from "@hooks/useSwapTokensBalances";
import useTokenBalanceForChain from "@hooks/useTokenBalanceForChain";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formatNumber } from "utils";
import { formatUnits } from "viem";
import { updateUnwrapTxnState } from "@store/slices/globalSlice";
// import AccordianItemWrapper from "../BridgeWrap/AccordianItemWrapper";
import { ALL_STEPS, BW_VARIANT, TChainSelection } from "./types";
import BridgeAccordionV2 from "./BridgeAccordionV2";
import WrapUnwrapAccordianV2 from "./WrapUnwrapAccordianV2";
import AccordianItemWrapperV2 from "./AccordianItemWrapperV2";

const MAIN_CHAIN_IDS = [1]; // Ethereum

// Category 1 chains use Transporter for bridging
const CATEGORY_1_CHAIN_IDS = [10, 59144, 48900]; // Optimism, Linea, Zircuit

// Category 2 chains use Stargate for bridging
const CATEGORY_2_CHAIN_IDS = [
  169, 81457, 34443, 534352, 8453, 324, 196, 42161, 239, 130, 57073, 988, 5000,
  4326,
]; // Manta Pacific, Blast, Mode, Scroll, Base, zkSync Era, X Layer, Arbitrum, Tac, Unichain, Ink, Mantle, megaeth

// Chains that natively support rsETH and don't need wrapping
const NATIVE_RSETH_CHAIN_IDS = [1, 42161, 130]; // Ethereum, Arbitrum, Unichain

interface BridgeJourneyStepsProps {
  sourceChain: TChainSelection;
  destinationChain: TChainSelection;
}

const BridgeJourneyStepsV2 = ({
  sourceChain,
  destinationChain,
}: BridgeJourneyStepsProps) => {
  const BRIDGE_WRAP_DATA = useBridgeWrapConfig(BW_VARIANT.RSETH);

  const isMainChain = (chainId: number) => MAIN_CHAIN_IDS.includes(chainId);
  const isUnwrapOnlyChain = (chainId: number) => chainId === 196; //X-layer
  const isCategory1Chain = (chainId: number) =>
    CATEGORY_1_CHAIN_IDS.includes(chainId);
  const isCategory2Chain = (chainId: number) =>
    CATEGORY_2_CHAIN_IDS.includes(chainId);
  const isNativeRsETHChain = (chainId: number) =>
    NATIVE_RSETH_CHAIN_IDS.includes(chainId);

  const { wrsETHBalance, rsETHBalance } = useSwapTokensBalances(
    sourceChain.id!
  );

  const dispatch = useDispatch();

  const txnSuccess = useSelector(
    (state: any) => state?.global?.unwrapTxnState?.success
  );

  useEffect(() => {
    if (txnSuccess) {
      dispatch(
        updateUnwrapTxnState({
          success: false,
        })
      );
    }
  }, [sourceChain, destinationChain, txnSuccess, dispatch]);

  const { xTokenTokenBalance } = useTokenBalanceForChain(sourceChain?.id!);

  const hasWrsETH =
    Number(toFixedWithoutRounding(formatUnits(wrsETHBalance, 18), 4)) > 0;

  const sourceBalance = isMainChain(sourceChain.id)
    ? Number(
        formatNumber(
          Number(xTokenTokenBalance),
          false,
          Number(xTokenTokenBalance) < 0.0001 ? 6 : 4,
          true
        )
      )
    : Number(formatUnits(rsETHBalance, 18));

  const assetSelected = hasWrsETH
    ? BRIDGE_WRAP_DATA.assets[1]
    : BRIDGE_WRAP_DATA.assets[0];

  const generateSteps = () => {
    const STEPS: string[] = [];

    // Behavior flags
    const isSourceMainChain = isMainChain(sourceChain.id);
    const isDestinationMainChain = isMainChain(destinationChain.id);
    const isSourceUnwrapOnly = isUnwrapOnlyChain(sourceChain.id);
    const isDestinationUnwrapOnly = isUnwrapOnlyChain(destinationChain.id);
    const isSourceCategory1 = isCategory1Chain(sourceChain.id);
    const isSourceCategory2 = isCategory2Chain(sourceChain.id);
    const isDestinationCategory1 = isCategory1Chain(destinationChain.id);
    const isDestinationCategory2 = isCategory2Chain(destinationChain.id);

    // Flag to determine if we should skip wrap/unwrap operations
    const shouldSkipWrapUnwrap =
      isSourceUnwrapOnly ||
      isDestinationUnwrapOnly ||
      isNativeRsETHChain(destinationChain.id);

    // Flag to determine if unwrapping is needed and allowed
    // Exclude native rsETH chains as they don't have wrapped tokens to unwrap
    const shouldUnwrap =
      (hasWrsETH || txnSuccess) &&
      !isDestinationUnwrapOnly &&
      !isNativeRsETHChain(sourceChain.id);

    // Same chain scenarios
    if (sourceChain.id === destinationChain.id) {
      if (!shouldSkipWrapUnwrap) {
        STEPS.push(ALL_STEPS.WRAP);
      }
      return STEPS;
    }

    // Category 1 to Category 2 multi-step bridging via Ethereum
    if (isSourceCategory1 && isDestinationCategory2) {
      if (shouldUnwrap) {
        STEPS.push(ALL_STEPS.UNWRAP);
      }
      STEPS.push(ALL_STEPS.BRIDGE_TO_ETH); // Category 1 → Ethereum (Transporter)
      STEPS.push(ALL_STEPS.BRIDGE_FROM_ETH); // Ethereum → Category 2 (Stargate)
      if (!shouldSkipWrapUnwrap) {
        STEPS.push(ALL_STEPS.WRAP);
      }
      return STEPS;
    }

    // Category 2 to Category 1 multi-step bridging via Ethereum
    if (isSourceCategory2 && isDestinationCategory1) {
      if (shouldUnwrap) {
        STEPS.push(ALL_STEPS.UNWRAP);
      }
      STEPS.push(ALL_STEPS.BRIDGE_TO_ETH); // Category 2 → Ethereum (Stargate)
      STEPS.push(ALL_STEPS.BRIDGE_FROM_ETH); // Ethereum → Category 1 (Transporter)
      if (!shouldSkipWrapUnwrap) {
        STEPS.push(ALL_STEPS.WRAP);
      }
      return STEPS;
    }

    // From main chain to non-main chain
    if (isSourceMainChain && !isDestinationMainChain) {
      STEPS.push(ALL_STEPS.BRIDGE);
      if (!shouldSkipWrapUnwrap) {
        STEPS.push(ALL_STEPS.WRAP);
      }
      return STEPS;
    }

    // From non-main chain to main chain
    if (!isSourceMainChain && isDestinationMainChain) {
      if (shouldUnwrap) {
        STEPS.push(ALL_STEPS.UNWRAP);
      }
      STEPS.push(ALL_STEPS.BRIDGE);
      return STEPS;
    }

    // From non-main chain to non-main chain
    if (!isSourceMainChain && !isDestinationMainChain) {
      if (shouldUnwrap) {
        STEPS.push(ALL_STEPS.UNWRAP);
      }
      STEPS.push(ALL_STEPS.BRIDGE);
      if (!shouldSkipWrapUnwrap) {
        STEPS.push(ALL_STEPS.WRAP);
      }
      return STEPS;
    }

    // Default fallback
    STEPS.push(ALL_STEPS.BRIDGE);
    return STEPS;
  };

  const renderStep = (step: string, idx: number) => {
    // Ethereum chain object for intermediate steps
    const ethereumChain = { id: 1, title: "Ethereum" };

    switch (step) {
      case ALL_STEPS.BRIDGE:
        return (
          <AccordianItemWrapperV2
            prefixLabel={`Step ${idx}:`}
            label={`Bridge your tokens`}
            value={`step-${idx}`}
            element={
              <BridgeAccordionV2
                fromChain={sourceChain}
                toChain={destinationChain}
                fromSymbol={BRIDGE_WRAP_DATA.assets[0].id}
                toSymbol={BRIDGE_WRAP_DATA.assets[0].id}
                tokenAmount={
                  sourceBalance
                    ? +toFixedWithoutRounding(sourceBalance.toString(), 4)
                    : 0
                }
              />
            }
          />
        );
      case ALL_STEPS.BRIDGE_TO_ETH:
        return (
          <AccordianItemWrapperV2
            prefixLabel={`Step ${idx}:`}
            label={`Bridge to Ethereum`}
            value={`step-${idx}`}
            element={
              <BridgeAccordionV2
                fromChain={sourceChain}
                toChain={ethereumChain}
                fromSymbol={BRIDGE_WRAP_DATA.assets[0].id}
                toSymbol={BRIDGE_WRAP_DATA.assets[0].id}
                tokenAmount={
                  sourceBalance
                    ? +toFixedWithoutRounding(sourceBalance.toString(), 4)
                    : 0
                }
              />
            }
          />
        );
      case ALL_STEPS.BRIDGE_FROM_ETH:
        return (
          <AccordianItemWrapperV2
            prefixLabel={`Step ${idx}:`}
            label={`Bridge from Ethereum`}
            value={`step-${idx}`}
            element={
              <BridgeAccordionV2
                fromChain={ethereumChain}
                toChain={destinationChain}
                fromSymbol={BRIDGE_WRAP_DATA.assets[0].id}
                toSymbol={BRIDGE_WRAP_DATA.assets[0].id}
                tokenAmount={null} // No balance for intermediate step
              />
            }
          />
        );
      case ALL_STEPS.WRAP:
        return (
          <AccordianItemWrapperV2
            prefixLabel={`Step ${idx}:`}
            label={`Wrap your tokens`}
            value={`step-${idx}`}
            element={
              <WrapUnwrapAccordianV2
                type="wrap"
                sourceChain={sourceChain}
                destinationChain={destinationChain}
              />
            }
          />
        );
      case ALL_STEPS.UNWRAP:
        return (
          <AccordianItemWrapperV2
            prefixLabel={`Step ${idx}:`}
            label={`Unwrap your tokens`}
            value={`step-${idx}`}
            element={
              <WrapUnwrapAccordianV2
                type="unwrap"
                sourceChain={sourceChain}
                destinationChain={destinationChain}
              />
            }
          />
        );
      default:
        return null;
    }
  };

  if (
    (sourceChain.id === 1 && destinationChain.id === 1) ||
    (sourceChain.id === 42161 && destinationChain.id === 42161) ||
    (sourceChain.id === 130 && destinationChain.id === 130)
  ) {
    return null;
  }

  return (
    <div className="flex flex-col gap-[10px] w-full">
      {/* {hasWrsETH && renderMessage()} */}
      {assetSelected?.id && (
        <Accordion
          type="multiple"
          defaultValue={["step-1", "step-2", "step-3"]}
          className="w-full flex flex-col"
        >
          {generateSteps()?.map((step, index) => {
            return (
              <>
                {/* {index > 0 && <hr className="bg-[#B3CECE] h-[1px]" />} */}
                {renderStep(step, index + 1)}
              </>
            );
          })}
        </Accordion>
      )}

      {/* {generateSteps().length > 0 && <hr className="bg-[#B3CECE] h-[1px]" />} */}
    </div>
  );
};

export default BridgeJourneyStepsV2;
